// The user's private notes on one repository (docs/design.md "Notes"),
// kept for the blobs the views ask for: a note names content, so a view
// asks for the notes on the blobs it shows and on the file's other blobs
// on its track, by path (`want`), and the server answers for exactly
// those pairs. Pairs once answered stay asked for: a later view of the
// same file costs nothing. A write's notice names the pairs it changed,
// and only those are looked up again (`heard`), the writing tab's own
// included (one small lookup, and no bookkeeping to go wrong). A lookup
// that fails is said (`error`) and its pairs are asked
// again by Try again (`retry`), or by the next view that wants them.
//
// Writes are optimistic, as drafts are: a new note shows at once (with a
// temporary negative ID until the server names it), an edit shows its new
// text, a delete hides the note; a write that fails is taken back and its
// error thrown to the caller, which says so and keeps what was typed.
// Promoting a note to a draft (`promote`) and keeping an agent's result
// (`keep`) show once the server answers: the first is the page's draft
// write too (state/promotion.ts), and the second is no one's typing.
import { isNotInstalled } from '../api/errors';
import {
  createNote,
  deleteNote,
  keepResultNote,
  lookupNotes,
  NOTES_PER_LOOKUP,
  promoteNote,
  restoreNote,
  updateNote,
  type NewNote,
  type NewPromotion,
  type Note,
  type ResultNote,
  type ReviewDraft,
} from '../api/server';

/** The notes API of one repository. */
export interface NotesApi {
  lookup(blobs: Record<string, string[]>): Promise<Note[]>;
  create(n: NewNote): Promise<Note>;
  update(id: number, body: string): Promise<Note>;
  remove(id: number): Promise<void>;
  restore(id: number): Promise<Note>;
  /** Keep an agent's result, once per task: null when its note was deleted since. */
  keep(n: ResultNote): Promise<Note | null>;
  promote(id: number, p: NewPromotion): Promise<{ note: Note; comment: ReviewDraft }>;
}

/** The changelist server's notes API for `owner/repo`. */
export function serverNotes(owner: string, repo: string): NotesApi {
  return {
    lookup: (blobs) => lookupNotes(owner, repo, blobs),
    create: (n) => createNote(owner, repo, n),
    update: (id, body) => updateNote(owner, repo, id, body),
    remove: (id) => deleteNote(owner, repo, id),
    restore: (id) => restoreNote(owner, repo, id),
    keep: (n) => keepResultNote(owner, repo, n),
    promote: (id, p) => promoteNote(owner, repo, id, p),
  };
}

const pairKey = (path: string, blob: string) => `${path}\0${blob}`;

/** `pairs` as lookup bodies of at most `NOTES_PER_LOOKUP` pairs each. */
function lookups(pairs: readonly [string, string][]): Record<string, string[]>[] {
  const out: Record<string, string[]>[] = [];
  for (let i = 0; i < pairs.length; i += NOTES_PER_LOOKUP) {
    const body: Record<string, string[]> = {};
    for (const [path, blob] of pairs.slice(i, i + NOTES_PER_LOOKUP)) (body[path] ??= []).push(blob);
    out.push(body);
  }
  return out;
}

/** Whether `a` and `b` are one note as written: same place, text and author. */
function sameNote(a: Note, b: Note): boolean {
  return (
    a.path === b.path &&
    a.side === b.side &&
    a.blob === b.blob &&
    a.start_line === b.start_line &&
    a.end_line === b.end_line &&
    a.body === b.body &&
    JSON.stringify(a.author) === JSON.stringify(b.author)
  );
}

function order(a: Note, b: Note): number {
  return a.path.localeCompare(b.path) || a.side.localeCompare(b.side) || a.blob.localeCompare(b.blob) || a.start_line - b.start_line || a.id - b.id;
}

export class NotesStore {
  /** The notes on the pairs asked for: the server's, with this tab's unanswered writes on top. */
  notes: readonly Note[] = $state.raw([]);
  /** The notes a write is saving (created, edited or deleted, not answered yet), by ID. */
  saving: ReadonlySet<number> = $state.raw(new Set());
  /** Why some notes could not be loaded; null while every pair asked for is loaded. */
  error: string | null = $state(null);

  /** The server's notes on the pairs asked for, by ID. */
  private server = new Map<number, Note>();
  /** The pairs asked for (`pairKey`), each with the ticket of the lookup last applied for it. */
  private wanted = new Map<string, number>();
  /** Lookups numbered as they start (see `fetch`). */
  private ticket = 0;
  /** Writes answered so far: a lookup in flight across one answers what may be stale, so it is asked again. */
  private landed = 0;
  /** Pairs whose last lookup failed (`retry` asks for them again). */
  private failed = new Set<string>();
  /** New notes being saved, each with the highest server ID known when it was started (a later one may be it). */
  private created: { note: Note; after: number }[] = [];
  private edits = new Map<number, string>();
  private removed = new Set<number>();
  private nextTemp = -1;
  private disposed = false;

  constructor(
    private api: NotesApi,
    /** Called, instead of reporting an error, when the server keeps nothing for the repository (403 `not_installed`). */
    private onNotInstalled: () => void = () => {},
  ) {}

  /** Show the notes on these blobs of these paths too: the pairs not asked for before are looked up. */
  async want(files: Iterable<readonly [string, Iterable<string>]>): Promise<void> {
    const fresh: [string, string][] = [];
    for (const [path, blobs] of files)
      for (const blob of blobs) {
        const k = pairKey(path, blob);
        if (this.wanted.has(k)) continue;
        this.wanted.set(k, 0);
        fresh.push([path, blob]);
      }
    if (fresh.length) await this.fetch(fresh);
  }

  /** Look up every pair asked for again (notices may have been missed). */
  async reload(): Promise<void> {
    const all = [...this.wanted.keys(), ...this.failed].map((k) => k.split('\0') as [string, string]);
    if (all.length) await this.fetch(all);
  }

  /** Look up the pairs whose last lookup failed again (Try again), whether they were answered before or never. */
  async retry(): Promise<void> {
    const again = [...this.failed].map((k) => k.split('\0') as [string, string]);
    for (const k of this.failed) if (!this.wanted.has(k)) this.wanted.set(k, 0);
    if (again.length) await this.fetch(again);
  }

  /**
   * A write, which a notice told of with the (path, blob) pairs it changed
   * (`pairs`): those asked for are looked up again. A notice naming no
   * pairs (an older server's) looks up every pair again.
   */
  async heard(n: { readonly [k: string]: unknown }): Promise<void> {
    if (!Array.isArray(n.pairs)) return this.reload();
    const named = (n.pairs as unknown[]).flatMap((p): [string, string][] => {
      const { path, blob } = (p ?? {}) as { path?: unknown; blob?: unknown };
      return typeof path === 'string' && typeof blob === 'string' && this.wanted.has(pairKey(path, blob)) ? [[path, blob]] : [];
    });
    if (named.length) await this.fetch(named);
  }

  /**
   * Look up `pairs` and apply the answer to each pair no later lookup has
   * answered. A write answered while a lookup was in flight may be missing
   * from that lookup's answer, so the lookup is made again.
   */
  private async fetch(pairs: readonly [string, string][]): Promise<void> {
    await Promise.all(
      lookups(pairs).map(async (body) => {
        for (;;) {
          const ticket = ++this.ticket;
          const landed = this.landed;
          let got: Note[];
          try {
            got = await this.api.lookup(body);
          } catch (e) {
            if (this.disposed) return;
            if (isNotInstalled(e)) return this.onNotInstalled();
            // Pairs never answered are not asked for any more, so the next view that wants them asks again.
            for (const [path, blobs] of Object.entries(body))
              for (const blob of blobs) {
                const k = pairKey(path, blob);
                this.failed.add(k);
                if (this.wanted.get(k) === 0) this.wanted.delete(k);
              }
            this.error = `Notes could not be loaded: ${e instanceof Error ? e.message : String(e)}`;
            return;
          }
          if (this.disposed) return;
          if (landed !== this.landed) continue;
          this.apply(body, got, ticket);
          for (const [path, blobs] of Object.entries(body)) for (const blob of blobs) this.failed.delete(pairKey(path, blob));
          if (!this.failed.size) this.error = null;
          return;
        }
      }),
    );
  }

  private apply(body: Record<string, string[]>, got: Note[], ticket: number) {
    const fresh = new Set<string>();
    for (const [path, blobs] of Object.entries(body))
      for (const blob of blobs) {
        const k = pairKey(path, blob);
        if ((this.wanted.get(k) ?? 0) > ticket) continue;
        this.wanted.set(k, ticket);
        fresh.add(k);
      }
    for (const [id, n] of this.server) if (fresh.has(pairKey(n.path, n.blob))) this.server.delete(id);
    for (const n of got) if (fresh.has(pairKey(n.path, n.blob))) this.server.set(n.id, n);
    this.recompute();
  }

  /** `notes` and `saving`, from the server's notes and the writes in flight. */
  private recompute() {
    const shown = [...this.server.values()]
      .filter((n) => this.wanted.has(pairKey(n.path, n.blob)) && !this.removed.has(n.id))
      .map((n) => (this.edits.has(n.id) ? { ...n, body: this.edits.get(n.id)! } : n));
    // A new note a lookup already brought back (saved, its answer still on its way) shows once.
    const pending = this.created.filter(({ note, after }) => !shown.some((n) => n.id > after && sameNote(n, note))).map((c) => c.note);
    this.notes = [...shown, ...pending].sort(order);
    this.saving = new Set([...this.edits.keys(), ...this.removed, ...this.created.map((c) => c.note.id)]);
  }

  /** A write to note `id` was answered: with `note` as the server now holds it, or gone. */
  private landedWith(id: number, note: Note | null) {
    this.landed++;
    if (note) {
      this.wanted.set(pairKey(note.path, note.blob), this.wanted.get(pairKey(note.path, note.blob)) ?? 0);
      this.server.set(note.id, note);
    } else this.server.delete(id);
  }

  /** Write a note: shown at once, and named by the server once it is saved. Throws when the server refuses it. */
  async create(n: NewNote): Promise<Note> {
    const now = new Date().toISOString();
    const temp: Note = { ...n, author: n.author ?? { kind: 'user' }, id: this.nextTemp--, promotion: null, source_task: null, created_at: now, updated_at: now };
    this.wanted.set(pairKey(n.path, n.blob), this.wanted.get(pairKey(n.path, n.blob)) ?? 0);
    const entry = { note: temp, after: Math.max(0, ...this.server.keys()) };
    this.created = [...this.created, entry];
    this.recompute();
    try {
      const saved = await this.api.create(n);
      if (!this.disposed) this.landedWith(saved.id, saved);
      return saved;
    } finally {
      this.created = this.created.filter((c) => c !== entry);
      this.recompute();
    }
  }

  /** Change a note's text: shown at once. Throws (and shows the old text again) when the server refuses it. */
  async edit(id: number, body: string): Promise<void> {
    this.edits.set(id, body);
    this.recompute();
    try {
      const saved = await this.api.update(id, body);
      if (!this.disposed) this.landedWith(id, saved);
    } finally {
      this.edits.delete(id);
      this.recompute();
    }
  }

  /** Delete a note: hidden at once, and restorable for a few minutes (`restore`). Throws (and shows it again) when the server refuses. */
  async remove(id: number): Promise<void> {
    this.removed.add(id);
    this.recompute();
    try {
      await this.api.remove(id);
      if (!this.disposed) this.landedWith(id, null);
    } finally {
      this.removed.delete(id);
      this.recompute();
    }
  }

  /** Bring back a note deleted a moment ago (Undo), as it was. */
  async restore(id: number): Promise<void> {
    const back = await this.api.restore(id);
    if (this.disposed) return;
    this.landedWith(id, back);
    this.recompute();
  }

  /**
   * Keep an agent's result as a note (once per task, whichever tab or
   * browser keeps it first): shown once the server answers. Null when the
   * user deleted that note since: it is not made again.
   */
  async keep(n: ResultNote): Promise<Note | null> {
    const kept = await this.api.keep(n);
    if (kept && !this.disposed) {
      this.landedWith(kept.id, kept);
      this.recompute();
    }
    return kept;
  }

  /**
   * Promote note `id` to a draft of a pull request: the server holds the
   * draft and records it on the note in one write. The note shows promoted
   * once that is answered; the draft is the caller's to show (the page's
   * draft write, state/promotion.ts). Throws, saying why, when refused.
   */
  async promote(id: number, p: NewPromotion): Promise<ReviewDraft> {
    const { note, comment } = await this.api.promote(id, p);
    if (!this.disposed) {
      this.landedWith(id, note);
      this.recompute();
    }
    return comment;
  }

  /** Stop, and drop the notes shown: the server keeps none for this repository any more. */
  forget() {
    unshare(this);
    this.dispose();
    this.server.clear();
    this.created = [];
    this.failed.clear();
    this.edits.clear();
    this.removed.clear();
    this.error = null;
    this.recompute();
  }

  /** Apply nothing from now on. */
  dispose() {
    this.disposed = true;
  }
}

/** A repository's notes store, shared by every open page of it (`acquireNotes`). */
interface Shared {
  store: NotesStore;
  /** Each holder's `onNotInstalled`. */
  holders: Set<() => void>;
}

/** The notes stores of the repositories some page shows, by `owner/repo` (lowercased). */
const shared = new Map<string, Shared>();

/**
 * The notes store of `owner/repo`, shared by every page that shows the
 * repository (a pull request's, a compare's, a branch's), so two views of
 * one repository show the same notes and one write shows in both. Each
 * holder releases it when it closes; the last release disposes it. A
 * holder's `onNotInstalled` runs when the server keeps nothing for the
 * repository; `forget` (that holder, or another) drops the store for
 * everyone, and the next acquire starts a new one.
 */
export function acquireNotes(owner: string, repo: string, onNotInstalled: () => void = () => {}): { store: NotesStore; release: () => void } {
  const key = `${owner}/${repo}`.toLowerCase();
  let s = shared.get(key);
  if (!s) {
    const holders = new Set<() => void>();
    const store = new NotesStore(serverNotes(owner, repo), () => {
      for (const h of [...holders]) h();
    });
    s = { store, holders };
    shared.set(key, s);
  }
  const mine = s;
  // A distinct holder each time, even for one callback given twice.
  const holder = () => onNotInstalled();
  mine.holders.add(holder);
  return {
    store: mine.store,
    release: () => {
      if (!mine.holders.delete(holder) || mine.holders.size) return;
      mine.store.dispose();
      if (shared.get(key) === mine) shared.delete(key);
    },
  };
}

/** Drop `store` from the shared ones, so the next acquire of its repository starts afresh (see `forget`). */
function unshare(store: NotesStore) {
  for (const [k, s] of shared) if (s.store === store) shared.delete(k);
}
